import { t } from '@lingui/macro'
import { AnimatePresence } from 'framer-motion'
import { Suspense } from 'react'
import { Outlet, useNavigate, useOutlet } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { NativePlatform } from '@/capacitor/components/native-platform/NativePlatform'
import { breakpoints } from '@/config/layout'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { ContentPagePaddingCSS } from '@/layout/components/content-page-layout/ContentPageLayout.styled'
import { mixpanel } from '@/mixpanel'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { openUrl } from '@/utils/external-resources'
import { CarePathCard } from '../../components/CarePathCard'
import { useCarePrograms } from '../../hooks/useCarePrograms'

const WELFARE_CONTACT_URL = 'https://www.mariamiddelares.be/nl/zorgaanbod/gezond-gent/contact'

const CareHomePage = () => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const outlet = useOutlet()
  const navigate = useNavigate()

  const { data: programs, isLoading } = useCarePrograms()

  // Methods
  // ------------------------------------------------------------------------- /
  const handleContact = () => {
    mixpanel.track('care_card_contact_click')
    openUrl(WELFARE_CONTACT_URL, { windowName: '_blank' })
  }

  // Render
  // ------------------------------------------------------------------------- /
  return (
    <ContentPageLayout
      noPadding
      noScroll
      header={
        <Suspense fallback={null}>
          <NativePlatform>
            <StMobileHeader>
              <AppHeader
                enableNotifications
                profileButtonEnabled
                centered={false}
                title={t({
                  id: 'care.title',
                  message: 'Welzijn',
                })}
              />
            </StMobileHeader>
          </NativePlatform>
        </Suspense>
      }
    >
      <AnimatePresence>
        {isLoading ? (
          <AnimatedLoader />
        ) : (
          <>
            <StContentContainer>
              <StIntroText type='BodyLargeRegular'>
                {t({
                  id: 'care.intro',
                  message: 'Laat je door ons team begeleiden naar een gezondere levensstijl.',
                })}
              </StIntroText>
              <ContentGrid>
                {programs?.map((program) => (
                  <CarePathCard key={program.id} program={program} />
                ))}

                <StLastCard>
                  <StLastCardContent>
                    <StLastCardTitle type='BodyLargeRegular'>
                      {t({
                        id: 'care.question.title',
                        message: 'Vind je niet wat je zoekt?',
                      })}
                    </StLastCardTitle>
                    <StLastCardDescription type='BodyLargeRegular'>
                      {t({
                        id: 'care.question.description',
                        message: 'We helpen je graag persoonlijk verder',
                      })}
                    </StLastCardDescription>
                    <Button variant='Primary' onClick={handleContact}>
                      {t({
                        id: 'care.question.cta',
                        message: 'Neem contact op',
                      })}
                    </Button>
                  </StLastCardContent>
                </StLastCard>
              </ContentGrid>
            </StContentContainer>
          </>
        )}
      </AnimatePresence>

      {/* Web only outlet for rendering inside a modal */}
      {outlet ? (
        <Modal isVisible={true} onClose={() => navigate(-1)} noPadding>
          <Outlet />
        </Modal>
      ) : null}
    </ContentPageLayout>
  )
}

const StMobileHeader = styled.div`
  ${ContentPagePaddingCSS}
  padding-bottom: 0;
`

const StContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space8};
  ${ContentPagePaddingCSS}

  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const ContentGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};

  /* Default for mobile (1 column) */
  grid-template-columns: 1fr;

  /* Ensure consistent heights */
  > * {
    height: 100%;
    // min-height: 0; /* Prevents overflow issues */
  }

  @media ${breakpoints.s} {
    grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
    gap: ${({ theme }) => theme.UI.SpacingPx.Space8}; /* 32px for desktop */
    > * {
      min-height: 200px;
    }
  }
`

const StLastCard = styled(Card)`
  object-fit: cover;
  overflow: hidden;

  flex: 1;
  display: flex;
`
const StLastCardContent = styled(FlexColumn)`
  position: relative;
  display: flex;
  flex-grow: 1;

  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};

  * {
    margin: 0;
  }
`

const LastCardTextCss = css`
  margin: 0;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StLastCardTitle = styled(P)`
  ${LastCardTextCss}
  font-weight: 700;
`

const StLastCardDescription = styled(P)`
  ${LastCardTextCss}
  font-weight: 4s00;
  display: flex;
  flex-grow: 1;
`

const StIntroText = styled(P)`
  @media ${breakpoints.m} {
    font-size: 22px;
    width: 75%;
  }
  line-height: 32px;
  margin: 0;
`

export { CareHomePage }
